import React, { useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import { CheckCircle, Clock, Video, FileText, AlertTriangle, Check } from 'lucide-react';
import videoCallPreview from '../../../../../../assets/images/graphics/video_call_preview.png';

export const StopRecordingCard: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [redone, setRedone] = useState(false);

  const pad = (n: number) => String(n).padStart(2, '0');

  if (submitted) {
    return (
      <Paper
        variant='outlined'
        sx={{
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          textAlign: 'center',
          p: 5,
        }}
      >
        <Stack spacing={2} alignItems='center'>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.50',
              color: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Check size={28} strokeWidth={3} />
          </Box>
          <Box>
            <Typography variant='subtitle1' fontWeight={700} color='text.primary'>
              Response Submitted!
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Your answer has been recorded and submitted successfully.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      {/* ── Question header ── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
          <Chip label='Question 1' size='small' variant='outlined' sx={{ fontSize: 10, height: 20 }} />
          <Chip label='Skill' size='small' color='success' sx={{ fontSize: 10, height: 20, fontWeight: 600 }} />
        </Stack>
        <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
          Explain the role of React in frontend development as an AEM Developer.
        </Typography>
      </Box>

      {/* ── Two-panel layout ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
          px: 2.5,
          pb: 2,
        }}
      >
        {/* Left – Question reference panel */}
        <Stack spacing={1}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
            }}
          >
            <FileText size={12} color='#1976d2' />
            <Typography variant='caption' fontWeight={700} color='primary.dark'>
              Question Reference
            </Typography>
          </Box>

          <Paper
            variant='outlined'
            sx={{
              bgcolor: 'action.hover',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 2,
              aspectRatio: '4/3',
              overflow: 'hidden',
            }}
          >
            <Box>
              <Chip
                label='SCENARIO'
                size='small'
                sx={{
                  fontSize: 8,
                  height: 16,
                  fontWeight: 700,
                  bgcolor: 'primary.100',
                  color: 'primary.dark',
                }}
              />
            </Box>
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10, lineHeight: 1.4 }}>
              You are a frontend developer joining a team that currently uses a
              monolithic jQuery codebase. The team is planning a migration to a
              modern component-based architecture.
            </Typography>
            <Stack
              spacing={0.5}
              sx={{
                borderLeft: '2px solid',
                borderColor: 'primary.light',
                pl: 1,
              }}
            >
              {[
                'Project has 50,000+ lines of legacy JS',
                'Team of 6 developers, mixed experience',
                'No existing test suite',
              ].map((point, i) => (
                <Typography key={i} variant='caption' color='text.secondary' sx={{ fontSize: 9 }}>
                  • {point}
                </Typography>
              ))}
            </Stack>
            <Box
              sx={{
                mt: 'auto',
                bgcolor: 'warning.50',
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 1,
                p: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <AlertTriangle size={12} color='#ed6c02' style={{ flexShrink: 0, marginTop: 2 }} />
              <Typography variant='caption' color='warning.dark' sx={{ fontSize: 9, lineHeight: 1.3 }}>
                Reference this scenario in your answer. No single correct answer exists.
              </Typography>
            </Box>
          </Paper>
        </Stack>

        {/* Right – Recorded video playback + actions */}
        <Stack spacing={1}>
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'common.black',
              borderRadius: 2,
              overflow: 'hidden',
              aspectRatio: '4/3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component='img'
              src={videoCallPreview}
              alt='Professional Video Call Preview'
              sx={{ height: '100%', width: 'auto', opacity: 0.7, objectFit: 'contain' }}
            />
            {/* Overlay */}
            <Stack
              spacing={0.5}
              alignItems='center'
              sx={{
                position: 'absolute',
                inset: 0,
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.3)',
              }}
            >
              <CheckCircle size={32} color='#4caf50' />
              <Typography variant='caption' fontWeight={700} color='success.light' sx={{ fontSize: 9, textTransform: 'uppercase' }}>
                Recording Securely Saved
              </Typography>
              <Typography variant='caption' sx={{ fontSize: 8, color: 'rgba(255,255,255,0.6)' }}>
                Review is disabled for privacy
              </Typography>
            </Stack>
          </Box>

          {/* Redo + Submit buttons */}
          <Stack direction='row' spacing={1}>
            <Button
              onClick={() => setRedone(true)}
              disabled={redone}
              variant='outlined'
              color='inherit'
              size='small'
              sx={{ flex: 1, fontSize: 10, py: 0.5, borderRadius: 5, textTransform: 'none' }}
            >
              {redone ? '✓ Redone' : 'Redo Recording'}
            </Button>
            <Button
              onClick={() => setSubmitted(true)}
              variant='contained'
              color='success'
              size='small'
              sx={{ flex: 1, fontSize: 10, py: 0.5, borderRadius: 5, textTransform: 'none' }}
            >
              Submit Response
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ── Bottom stats bar ── */}
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        sx={{ px: 2.5, pb: 2 }}
      >
        {/* Timer — exhausted */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ height: 4, width: '100%', bgcolor: 'primary.main', borderRadius: 2, mb: 1 }} />
          <Stack direction='row' spacing={1} alignItems='center'>
            <Clock size={14} color='#1976d2' />
            <Typography variant='body2' fontWeight={700} color='text.primary'>
              {pad(0)}:{pad(0)}
            </Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Remaining Time
          </Typography>
        </Box>

        {/* Skill questions */}
        <Paper
          variant='outlined'
          sx={{
            px: 2,
            py: 1,
            textAlign: 'center',
            borderRadius: 2,
            borderColor: 'primary.light',
            minWidth: 80,
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 8, textTransform: 'uppercase' }}>
            Skill Questions
          </Typography>
          <Typography variant='h6' fontWeight={800} color='text.primary' sx={{ lineHeight: 1 }}>
            1
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 8, textTransform: 'uppercase' }}>
            Left to Answer
          </Typography>
        </Paper>

        {/* Video tips */}
        <Stack spacing={0.5} sx={{ minWidth: 100 }}>
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <Video size={12} color='#2e7d32' />
            <Typography variant='caption' fontWeight={700} color='text.primary' sx={{ fontSize: 9, textTransform: 'uppercase' }}>
              Video Tips
            </Typography>
          </Stack>
          {[
            'Stable internet required',
            'Quiet, well-lit environment',
            'Re-recording allowed',
          ].map((tip, i) => (
            <Stack key={i} direction='row' spacing={0.5} alignItems='center'>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: 9 }}>
                {tip}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};
